import { useCallback, useEffect, useState } from "react";
import { AlertCircle, BrainCircuit, CheckCircle2, RefreshCw, ShieldCheck } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Skeleton } from "./ui/skeleton";
import ppaIntegrationService, { safePPAErrorMessage } from "../services/ppaIntegrationService";
import { ppaPresentation } from "./ppaPresentation";
import { ppaDocumentPresentation } from "./ppaDocumentPresentation";

const PPAOpportunityIntelligence = ({ opportunity, health, open, onOpenChange }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [documentData, setDocumentData] = useState(null);
  const [documentError, setDocumentError] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [proposing, setProposing] = useState(false);
  const [proposal, setProposal] = useState(null);

  const load = useCallback(async () => {
    if (!opportunity?.id || !health?.enabled) return;
    setLoading(true);
    setError("");
    setDocumentError("");
    try {
      setData(await ppaIntegrationService.readOpportunity(opportunity.id));
      if (health?.documentIntelligenceEnabled) {
        try {
          setDocumentData(await ppaIntegrationService.readDocumentIntelligence(opportunity.id));
        } catch (requestError) {
          setDocumentData(null);
          setDocumentError(safePPAErrorMessage(requestError));
        }
      } else {
        setDocumentData(null);
      }
    } catch (requestError) {
      setData(null);
      setError(safePPAErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, [health?.documentIntelligenceEnabled, health?.enabled, opportunity?.id]);

  useEffect(() => {
    if (open) {
      setProposal(null);
      load();
    }
  }, [load, open]);

  const createProposal = async () => {
    if (proposing) return;
    setProposing(true);
    setError("");
    try {
      setProposal(await ppaIntegrationService.proposePursuit(opportunity.id, "track"));
      setConfirmOpen(false);
    } catch (requestError) {
      setError(safePPAErrorMessage(requestError));
    } finally {
      setProposing(false);
    }
  };

  const view = ppaPresentation(data);
  const documentView = ppaDocumentPresentation(documentData);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-card max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BrainCircuit className="h-5 w-5 text-primary" />
              PPA Intelligence
            </DialogTitle>
            <p className="text-sm text-muted-foreground">{opportunity?.name}</p>
          </DialogHeader>

          {loading && (
            <div className="space-y-3" data-testid="ppa-loading">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-28 w-full" />
            </div>
          )}

          {!loading && error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Intelligence unavailable</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {!loading && data && (
            <div className="space-y-4">
              <div className="rounded-lg border border-border/50 p-4 grid gap-3 sm:grid-cols-2">
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Intelligence</p>
                  <p className="font-medium">{view.available ? "Available" : "Not available yet"}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Orchestration</p>
                  <Badge variant="outline">{view.status}</Badge>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Documents</p>
                  <p className="font-medium">{view.coverage}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Graph version</p>
                  <p className="font-medium">{view.graphVersion ?? "Pending"}</p>
                </div>
              </div>

              {view.needsAttention && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Review recommended</AlertTitle>
                  <AlertDescription>
                    {view.accessRequired
                      ? "One or more documents require authorized operator access before the package can be complete."
                      : "The intelligence package needs operator attention before use."}
                  </AlertDescription>
                </Alert>
              )}

              {view.refreshAvailable && (
                <Alert>
                  <RefreshCw className="h-4 w-4" />
                  <AlertTitle>New intelligence available</AlertTitle>
                  <AlertDescription>Refresh this read to load the latest completed graph.</AlertDescription>
                </Alert>
              )}

              {view.busy && !view.needsAttention && (
                <p className="text-sm text-muted-foreground">
                  PPA is still processing this opportunity. The current read remains read-only.
                </p>
              )}

              <div className="rounded-lg border border-border/50 p-4 text-sm">
                <p className="font-medium mb-1">Resolved identity</p>
                <p className="text-muted-foreground">Solicitation: {data.identity.solicitationNumber || "Not provided"}</p>
                <p className="text-muted-foreground">Generated: {view.generatedAt ? new Date(view.generatedAt).toLocaleString() : "Pending"}</p>
              </div>

              {health?.documentIntelligenceEnabled && documentError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Document intelligence unavailable</AlertTitle>
                  <AlertDescription>{documentError}</AlertDescription>
                </Alert>
              )}

              {health?.documentIntelligenceEnabled && documentData && (
                <div className="rounded-lg border border-border/50 p-4 space-y-3 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-medium">Evidence-backed project scope</p>
                    <Badge variant="outline">{documentView.state}</Badge>
                  </div>
                  <p className="text-muted-foreground">{documentView.scopeSummary}</p>
                  <p className="text-muted-foreground">
                    Read {documentView.counts.read} of {documentView.counts.discovered} discovered documents
                  </p>
                  {documentView.missingDocuments.length > 0 && (
                    <div>
                      <p className="font-medium">Missing or inaccessible</p>
                      <ul className="list-disc pl-5 text-muted-foreground">
                        {documentView.missingDocuments.map((item) => <li key={item}>{item}</li>)}
                      </ul>
                    </div>
                  )}
                  {documentView.humanActions.length > 0 && (
                    <div>
                      <p className="font-medium">Operator action required</p>
                      <ul className="list-disc pl-5 text-muted-foreground">
                        {documentView.humanActions.map((item) => <li key={item}>{item}</li>)}
                      </ul>
                    </div>
                  )}
                  <div className="grid gap-2 sm:grid-cols-3 text-muted-foreground">
                    <span>{documentView.criticalDates.length} critical dates</span>
                    <span>{documentView.requirements.length} compliance items</span>
                    <span>{documentView.risks.length} key risks</span>
                  </div>
                </div>
              )}

              {proposal && (
                <Alert>
                  <ShieldCheck className="h-4 w-4" />
                  <AlertTitle>Proposal prepared—no pursuit created</AlertTitle>
                  <AlertDescription>
                    Proposal {proposal.proposalId.slice(0, 12)} is awaiting explicit operator confirmation.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
            <Button variant="outline" onClick={load} disabled={loading}>
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Refresh read
            </Button>
            {health?.pursuitProposalsEnabled && data && !proposal && (
              <Button onClick={() => setConfirmOpen(true)} disabled={proposing}>
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Prepare pursuit proposal
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Prepare a pursuit proposal?</AlertDialogTitle>
            <AlertDialogDescription>
              This creates an idempotent proposal for review. It does not create a pursuit, project, or remote write.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={proposing}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={createProposal} disabled={proposing}>
              {proposing ? "Preparing…" : "Prepare proposal"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default PPAOpportunityIntelligence;
